/**
 * BuildHub form hook with background creation and queued updates
 *
 * This hook allows the user to continue filling out the form while BuildHub creation
 * happens in the background. Any updates made before the ID arrives are queued and
 * applied in a single batch once the ID is available.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import type { BuildHubFormData } from '../api/types';
import type { ValidationResult } from '../validation/schemas';
import { validateStepData } from '../validation/schemas';
import { BUILD_HUB_UPSERT_API_ROUTE } from '~/routes/remix-api.app.build-hub-create';
import type {
    CreateOrUpdateBuildHubMutation,
    BuildHubTypeEnum,
} from 'generated/graphql';
import { buildFormDataForUpdate } from '~/modules/build-hub/utils/build-form-data';
import { useBuildHubUpdate } from '@modules/build-hub/hooks/use-build-hub-update';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import type { StageInput } from '@modules/build-hub/utils/build-form-data';
import type { GroupedSelectOptionType } from '@modules/hde/utils/form-options';

/**
 * Actual location data structure from the form
 */
interface ActualLocationData {
    selectedLocation: DetailedPlace;
    keyword: string;
}

/**
 * HDE registration data collected in the modal
 */
export interface HDERegistrationData {
    projectAttendanceReason?: GroupedSelectOptionType[];
    hdeOtherPleaseSpecify?: string;
    projectProductInterest?: GroupedSelectOptionType[];
    hearAboutMGE?: GroupedSelectOptionType;
    location?: GroupedSelectOptionType;
    locationOptions?: GroupedSelectOptionType[];
}

/**
 * Authentication data - stored for reference but user logs in/signs up directly in step
 * Phone is collected during signup for HDE registration which requires it
 */
interface AuthenticationData {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
}

/**
 * Simple form data state - just the form fields
 */
interface FormState {
    title: string;
    description?: string;
    projectType?: BuildHubTypeEnum;
    projectScope?: string; // Display label (e.g., "New Home Build")
    stages?: StageInput[];
    activeStageURLSegment?: string;
    ownerType?: 'own-property' | 'client-project';
    ownerTeamMemberID?: string;
    ownerCustomCompanyName?: string;
    scope?: BuildHubFormData['scope'];
    location?: BuildHubFormData['location'] | ActualLocationData;
    timeline?: BuildHubFormData['timeline'];
    budget?: BuildHubFormData['budget'];
    thumbnailFile?: File;
    stageConfig?: StageConfiguration;
    hdeRegistrationData?: HDERegistrationData;
    authenticationData?: AuthenticationData;
}

interface UseBuildHubFormOptions {
    onSuccess?: () => void;
    shouldNavigateOnSuccess?: boolean;
    /** Recorded on BuildHub.CreationSource when the project is first created. */
    creationSource?: string;
}

/**
 * Return type for the hook
 */
interface UseBuildHubFormReturn {
    // Simple state
    formData: FormState;
    buildHubId: string | null;
    isCreationPending: boolean; // True if creation started but ID not received yet
    isSubmitSuccess: boolean;

    // Loading states
    isCreating: boolean;
    isSubmitting: boolean;

    // Error states
    createError: string | null;
    submitError: string | null;

    // Actions
    updateField: (field: keyof FormState, value: unknown) => void;
    createProject: (title: string) => void;
    submitProject: () => void;

    // Validation
    validateStep: (stepId: string, data: unknown) => ValidationResult;
}

/**
 * BuildHub form hook with background creation
 *
 * Workflow:
 * 1. Project scope step: createProject() → BuildHub creation starts in background
 * 2. User continues immediately: updateField() queues changes
 * 3. When ID arrives: Queued updates are applied as a single mutation
 * 4. Final step: submitProject() → final update → redirect
 */
export function useBuildHubForm(
    options: UseBuildHubFormOptions = {},
): UseBuildHubFormReturn {
    const {
        onSuccess,
        shouldNavigateOnSuccess = true,
        creationSource,
    } = options;

    // Simple local state
    const [formData, setFormData] = useState<FormState>({
        title: '',
    });
    const [buildHubId, setBuildHubId] = useState<string | null>(null);
    const [isCreationPending, setIsCreationPending] = useState(false);

    // Queue to store updates made while waiting for BuildHub ID
    const [updateQueue, setUpdateQueue] = useState<Partial<FormState>>({});

    // Flag to track if we've already applied queued updates
    const hasAppliedQueueRef = useRef(false);

    // Refs to hold latest values for stable callbacks
    const formDataRef = useRef(formData);
    const buildHubIdRef = useRef(buildHubId);

    // Update refs when state changes
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        buildHubIdRef.current = buildHubId;
    }, [buildHubId]);

    // Create fetcher for project creation
    const createFetcher = useFetcher<CreateOrUpdateBuildHubMutation>();

    // Separate fetcher for applying queued updates (doesn't navigate)
    const queuedUpdateFetcher = useFetcher<CreateOrUpdateBuildHubMutation>();

    // Update hook for final submission (reuses our utility)
    const {
        updateBuildHub,
        isUpdating: isSubmitting,
        error: submitError,
        isSuccess: isSubmitSuccess,
    } = useBuildHubUpdate({
        onSuccess,
        shouldNavigateOnSuccess,
    });

    // Derived loading state
    const isCreating = createFetcher.state !== 'idle';

    // Handle create response and apply queued updates
    useEffect(() => {
        if (createFetcher.state === 'idle' && createFetcher.data) {
            const buildHub = createFetcher.data.buildHubUpsert;
            if (buildHub?.__typename === 'BuildHub' && buildHub.ID) {
                console.log('✅ BuildHub created with ID:', buildHub.ID);
                setBuildHubId(buildHub.ID);
                setIsCreationPending(false);

                // Sync title from server response
                setFormData((prev) => ({
                    ...prev,
                    title: buildHub.Title || prev.title,
                }));

                // Apply queued updates if any exist and haven't been applied yet
                if (
                    Object.keys(updateQueue).length > 0 &&
                    !hasAppliedQueueRef.current
                ) {
                    console.log('📦 Applying queued updates:', updateQueue);
                    hasAppliedQueueRef.current = true;

                    // Build FormData with queued updates
                    const formDataObj = buildFormDataForUpdate(buildHub.ID, {
                        ...updateQueue,
                    });

                    // Apply the queued updates using a fetcher that doesn't navigate
                    queuedUpdateFetcher.submit(formDataObj, {
                        method: 'post',
                        action: BUILD_HUB_UPSERT_API_ROUTE,
                    });

                    // Clear the queue
                    setUpdateQueue({});
                }
            }
        }
    }, [
        createFetcher.state,
        createFetcher.data,
        updateQueue,
        queuedUpdateFetcher,
    ]);

    /**
     * Field update function with queuing support
     * If BuildHub ID is not available yet, updates are queued
     */
    const updateField = useCallback(
        (field: keyof FormState, value: unknown) => {
            // Always update local state immediately for UI responsiveness
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));

            // If creation is pending (no ID yet), queue this update
            if (isCreationPending && !buildHubId) {
                console.log(`📝 Queuing update: ${field}`, value);
                setUpdateQueue((prev) => ({
                    ...prev,
                    [field]: value,
                }));
            }
        },
        [isCreationPending, buildHubId],
    );

    /**
     * Create BuildHub in background
     * This now happens after project scope is selected, allowing user to continue immediately
     */
    const createProject = useCallback(
        (title: string) => {
            console.log('🚀 Starting background BuildHub creation');

            // Mark creation as pending
            setIsCreationPending(true);
            hasAppliedQueueRef.current = false; // Reset the flag for new creation

            // Update local state
            setFormData((prev) => ({ ...prev, title }));

            // Build FormData with all current data
            const formDataObj = new FormData();
            formDataObj.append('intent', 'create');
            formDataObj.append('title', title);
            if (creationSource) {
                formDataObj.append('creationSource', creationSource);
            }

            // Include project type and scope if available
            const currentFormData = formDataRef.current;
            if (currentFormData.projectType) {
                formDataObj.append('projectType', currentFormData.projectType);
            }
            if (currentFormData.projectScope) {
                formDataObj.append(
                    'projectScope',
                    currentFormData.projectScope,
                );
            }
            if (currentFormData.stages) {
                formDataObj.append(
                    'stages',
                    JSON.stringify(currentFormData.stages),
                );
            }
            if (currentFormData.activeStageURLSegment) {
                formDataObj.append(
                    'activeStageURLSegment',
                    currentFormData.activeStageURLSegment,
                );
            }
            if (currentFormData.ownerTeamMemberID) {
                formDataObj.append(
                    'ownerTeamMemberID',
                    currentFormData.ownerTeamMemberID,
                );
            }
            if (currentFormData.ownerCustomCompanyName) {
                formDataObj.append(
                    'ownerCustomCompanyName',
                    currentFormData.ownerCustomCompanyName.trim(),
                );
            }

            // Submit create request
            createFetcher.submit(formDataObj, {
                method: 'post',
                action: BUILD_HUB_UPSERT_API_ROUTE,
            });
        },
        [createFetcher, creationSource],
    );

    /**
     * Step 6: Submit all form data to update BuildHub
     * Now uses the reusable utility function
     */
    const submitProject = useCallback(() => {
        if (!buildHubId) {
            console.error('No BuildHub ID available for update');
            return;
        }

        console.log('🔍 Submitting project with stages:', formData.stages);

        try {
            // Use the utility function to build FormData
            const formDataObj = buildFormDataForUpdate(buildHubId, {
                title: formData.title,
                description: formData.description,
                projectType: formData.projectType,
                scope: formData.projectScope, // Use display label directly
                stages: formData.stages,
                activeStageURLSegment: formData.activeStageURLSegment,
                ownerTeamMemberID: formData.ownerTeamMemberID,
                ownerCustomCompanyName: formData.ownerCustomCompanyName,
                location: formData.location,
                timeline: formData.timeline,
                budget: formData.budget,
                thumbnailFile: formData.thumbnailFile,
            });

            // Submit using the reusable hook
            updateBuildHub(formDataObj);
        } catch (error) {
            console.error('Failed to build form data for submission:', error);
        }
    }, [buildHubId, formData, updateBuildHub]);

    /**
     * Validation function
     */
    const validateStep = useCallback(
        (stepId: string, data: unknown): ValidationResult => {
            return validateStepData(stepId, data);
        },
        [],
    );

    // Extract error messages for creation
    const createError = useMemo(() => {
        if (createFetcher.state === 'idle' && createFetcher.data) {
            const buildHub = createFetcher.data.buildHubUpsert;
            if (buildHub?.__typename !== 'BuildHub') {
                return (
                    (buildHub as { Message?: string })?.Message ||
                    'Failed to create project'
                );
            }
        }
        return null;
    }, [createFetcher.state, createFetcher.data]);

    return {
        formData,
        buildHubId,
        isCreationPending,
        isSubmitSuccess,
        isCreating,
        isSubmitting,
        createError,
        submitError,
        updateField,
        createProject,
        submitProject,
        validateStep,
    };
}
